// components/ui/confirmation-dialog.tsx
import { useIntl } from 'react-intl';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

import type { ConfirmVariant } from '@/components/base/confirmation';

const CONFIRM_BUTTON_VARIANT_CLASSES: Record<
  NonNullable<ConfirmVariant>,
  string
> = {
  default: '',
  destructive: 'bg-fx-rose',
};

interface ConfirmationDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  cancelLabel?: string;
  confirmLabel?: string;
  confirmVariant?: ConfirmVariant;
  onConfirm: () => void;
  loading?: boolean;
}

export function ConfirmationDialog({
  open,
  onOpenChange,
  title,
  description,
  cancelLabel,
  confirmLabel,
  confirmVariant = 'default',
  onConfirm,
  loading = false,
}: ConfirmationDialogProps) {
  const intl = useIntl();

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>{title}</AlertDialogTitle>
          {description && (
            <AlertDialogDescription>{description}</AlertDialogDescription>
          )}
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={loading}>
            {cancelLabel ?? intl.formatMessage({ defaultMessage: 'Cancel' })}
          </AlertDialogCancel>
          <AlertDialogAction
            className={
              CONFIRM_BUTTON_VARIANT_CLASSES[confirmVariant] || undefined
            }
            onClick={onConfirm}
            disabled={loading}
          >
            {confirmLabel ?? intl.formatMessage({ defaultMessage: 'Confirm' })}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
